import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
    Avatar,
    Box,
    Chip,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    Tooltip,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../../context/Auth/AuthContext';
import socket from '../../hooks/useSocket';
import api from '../../services/api';
import MessagesReportModal from '../MessagesReportModal';

// Container principal
const Container = styled(Box)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '12px',
    border: `1px solid ${theme.palette.divider}`,
    overflow: 'hidden',
}));

// Header
const Header = styled(Box)(({ theme }) => ({
    padding: '10px 16px',
    backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#fafafa',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const HeaderInfo = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
});

const Title = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
}));

const Subtitle = styled(Typography)(({ theme }) => ({
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
}));

const CountBadge = styled(Box)(({ theme }) => ({
    minWidth: '32px',
    height: '28px',
    padding: '0 8px',
    borderRadius: '6px',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8rem',
    fontWeight: 600,
}));

// Lista de tickets
const TicketList = styled(List)(({ theme }) => ({
    flex: 1,
    overflow: 'auto',
    padding: '4px 6px',
    margin: 0,
    '&::-webkit-scrollbar': {
        width: '5px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.mode === 'dark' ? '#3a3a3a' : '#d0d0d0',
        borderRadius: '3px',
    },
}));

// Card do ticket
const TicketCard = styled(ListItem)(({ theme }) => ({
    padding: '8px 10px',
    marginBottom: '4px',
    borderRadius: '6px',
    backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#fff',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
        borderColor: theme.palette.primary.main,
    },
    '&:last-child': {
        marginBottom: 0,
    },
}));

// Informações do ticket
const TicketContent = styled(Box)({
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
});

const TicketHeader = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '6px',
});

const ContactName = styled(Typography)(({ theme }) => ({
    fontSize: '0.8rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
}));

const Position = styled(Typography)(({ theme }) => ({
    fontSize: '0.65rem',
    fontWeight: 500,
    color: theme.palette.text.disabled,
    flexShrink: 0,
}));

// Meta informações
const MetaRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    flexWrap: 'wrap',
});

const TimeBadge = styled(Box)(({ urgency, theme }) => {
    const colors = {
        low: { bg: theme.palette.mode === 'dark' ? '#1b3329' : '#e8f5e9', text: '#4caf50' },
        medium: { bg: theme.palette.mode === 'dark' ? '#3d2e1a' : '#fff3e0', text: '#f57c00' },
        high: { bg: theme.palette.mode === 'dark' ? '#3d1a1a' : '#ffebee', text: '#d32f2f' },
    };
    const color = colors[urgency] || colors.low;

    return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '3px',
        padding: '2px 6px',
        borderRadius: '4px',
        fontSize: '0.65rem',
        fontWeight: 600,
        color: color.text,
        backgroundColor: color.bg,
    };
});

const QueueChip = styled(Chip)({
    height: '18px',
    fontSize: '0.6rem',
    fontWeight: 600,
    '& .MuiChip-label': {
        padding: '0 5px',
    },
});

const ConnectionInfo = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    fontSize: '0.6rem',
    color: theme.palette.text.secondary,
    '& svg': {
        fontSize: '11px',
        color: '#25D366',
    },
}));

const LastMessage = styled(Typography)(({ theme }) => ({
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

// Botão de espiar
const SpyButton = styled(IconButton)(({ theme }) => ({
    padding: '4px',
    color: theme.palette.text.secondary,
    '&:hover': {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(25, 118, 210, 0.1)'
            : 'rgba(25, 118, 210, 0.08)',
    },
}));

// Estado vazio
const EmptyState = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    color: theme.palette.text.secondary,
    textAlign: 'center',
    flex: 1,
}));

const PendingTicketsMonitor = ({ selectedQueueIds = [] }) => {
    const { user } = useContext(AuthContext);
    const [tickets, setTickets] = useState([]);
    const [timeTick, setTimeTick] = useState(0);
    const [messagesModalOpen, setMessagesModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const initialLoadDone = useRef(false);

    const fetchPendingTickets = useCallback(async () => {
        try {
            const { data } = await api.get('/tickets', {
                params: {
                    status: 'pending',
                    showAll: true,
                    pageNumber: 1,
                    queueIds: JSON.stringify(selectedQueueIds.length > 0 ? selectedQueueIds : []),
                }
            });
            const sortedTickets = (data.tickets || []).sort((a, b) =>
                new Date(a.createdAt) - new Date(b.createdAt)
            );
            setTickets(sortedTickets);
            initialLoadDone.current = true;
        } catch (error) {
            console.error('Error fetching pending tickets:', error);
        }
    }, [selectedQueueIds]);

    useEffect(() => {
        fetchPendingTickets();
        const intervalId = setInterval(fetchPendingTickets, 30000);
        return () => clearInterval(intervalId);
    }, [fetchPendingTickets]);

    useEffect(() => {
        if (!user?.companyId) return;

        const handleTicketUpdate = (data) => {
            if (data.action === 'update' || data.action === 'create') {
                if (data.ticket?.status === 'pending') {
                    const queueMatch = selectedQueueIds.length === 0 ||
                        selectedQueueIds.includes(data.ticket.queueId);

                    if (queueMatch) {
                        setTickets(prev => {
                            const exists = prev.find(t => t.id === data.ticket.id);
                            let updated = exists
                                ? prev.map(t => t.id === data.ticket.id ? data.ticket : t)
                                : [...prev, data.ticket];
                            return updated.sort((a, b) =>
                                new Date(a.createdAt) - new Date(b.createdAt)
                            );
                        });
                    }
                } else {
                    setTickets(prev => prev.filter(t => t.id !== data.ticket?.id));
                }
            }
            if (data.action === 'delete') {
                setTickets(prev => prev.filter(t => t.id !== data.ticketId));
            }
        };

        const handleAppMessage = (data) => {
            if (data?.action === 'create' && data?.ticket?.status === 'pending') {
                setTickets(prev => {
                    const exists = prev.find(t => t.id === data.ticket.id);
                    if (exists) {
                        return prev.map(t =>
                            t.id === data.ticket.id
                                ? { ...t, lastMessage: data.message?.body, updatedAt: new Date().toISOString() }
                                : t
                        ).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                    }
                    return prev;
                });
            }
        };

        socket.on(`ticket-${user.companyId}`, handleTicketUpdate);
        socket.on(`appMessage-${user.companyId}`, handleAppMessage);

        return () => {
          //  socket.off(`ticket-${user.companyId}`, handleTicketUpdate);
           // socket.off(`appMessage-${user.companyId}`, handleAppMessage);
        };
    }, [user?.companyId, selectedQueueIds]);

    const calculateWaitingTime = (createdAt) => {
        const now = new Date();
        const created = new Date(createdAt);
        const diffMs = now - created;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);
        const diffDays = Math.floor(diffHours / 24);

        if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
        if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m`;
        return `${diffMins}m`;
    };

    const getUrgencyLevel = (createdAt) => {
        const diffMins = Math.floor((new Date() - new Date(createdAt)) / 60000);
        if (diffMins > 60) return 'high';
        if (diffMins > 30) return 'medium';
        return 'low';
    };

    const handleSpyTicket = (e, ticket) => {
        e.stopPropagation();
        setSelectedTicket(ticket);
        setMessagesModalOpen(true);
    };

    const handleCloseMessagesModal = () => {
        setMessagesModalOpen(false);
        setSelectedTicket(null);
    };

    // Incrementa tick para atualizar tempos de espera sem recriar array
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeTick(t => t + 1);
        }, 60000);
        return () => clearInterval(timer);
    }, []);

    // timeTick força recálculo dos tempos de espera a cada 60s
    const filteredTickets = useMemo(() => {
        void timeTick; // referência para trigger de re-render
        return selectedQueueIds.length > 0
            ? tickets.filter(t => selectedQueueIds.includes(t.queueId) || !t.queueId)
            : tickets;
    }, [tickets, selectedQueueIds, timeTick]);

    return (
        <Container>
            <Header>
                <HeaderInfo>
                    <Title>Fila de Espera</Title>
                    <Subtitle>{filteredTickets.length} aguardando</Subtitle>
                </HeaderInfo>
                <CountBadge>{filteredTickets.length}</CountBadge>
            </Header>

            {filteredTickets.length === 0 ? (
                <EmptyState>
                    <AccessTimeIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                    <Typography variant="body2" fontWeight={500}>Nenhum na fila</Typography>
                </EmptyState>
            ) : (
                <TicketList>
                    {filteredTickets.map((ticket, index) => {
                        const urgency = getUrgencyLevel(ticket.createdAt);
                        return (
                            <TicketCard
                                key={ticket.id}
                                onClick={(e) => handleSpyTicket(e, ticket)}
                            >
                                <ListItemAvatar sx={{ minWidth: 'auto' }}>
                                    <Avatar
                                        src={ticket.contact?.profilePicUrl}
                                        alt={ticket.contact?.name}
                                        sx={{ width: 32, height: 32, fontSize: '0.8rem' }}
                                    >
                                        {ticket.contact?.name?.charAt(0)?.toUpperCase()}
                                    </Avatar>
                                </ListItemAvatar>

                                <TicketContent>
                                    <TicketHeader>
                                        <ContactName>
                                            {ticket.contact?.name || ticket.contact?.number || 'Sem nome'}
                                        </ContactName>
                                        <Position>#{index + 1}</Position>
                                    </TicketHeader>

                                    <MetaRow>
                                        <TimeBadge urgency={urgency}>
                                            <AccessTimeIcon sx={{ fontSize: 10 }} />
                                            {calculateWaitingTime(ticket.createdAt)}
                                        </TimeBadge>

                                        {ticket.queue && (
                                            <QueueChip
                                                label={ticket.queue.name}
                                                size="small"
                                                sx={{
                                                    backgroundColor: ticket.queue.color || '#666',
                                                    color: '#fff',
                                                }}
                                            />
                                        )}

                                        {ticket.whatsapp && (
                                            <ConnectionInfo>
                                                <WhatsAppIcon />
                                                {ticket.whatsapp.name}
                                            </ConnectionInfo>
                                        )}
                                    </MetaRow>

                                    {ticket.lastMessage && (
                                        <LastMessage>{ticket.lastMessage}</LastMessage>
                                    )}
                                </TicketContent>

                                <Tooltip title="Espiar" placement="left">
                                    <SpyButton
                                        size="small"
                                        onClick={(e) => handleSpyTicket(e, ticket)}
                                    >
                                        <VisibilityIcon sx={{ fontSize: 16 }} />
                                    </SpyButton>
                                </Tooltip>
                            </TicketCard>
                        );
                    })}
                </TicketList>
            )}

            <MessagesReportModal
                open={messagesModalOpen}
                onClose={handleCloseMessagesModal}
                ticket={selectedTicket}
            />
        </Container>
    );
};

// Memoizado: o pai (Monitor) re-renderiza com frequência. Sem memo todos os
// tickets pendentes (incluindo seus Tooltip+Popper) re-renderizam junto.
// A única prop é selectedQueueIds; comparação rasa do React.memo basta.
export default React.memo(PendingTicketsMonitor);
